import { useMemo } from "react";
import { AdminLayout } from "./AdminLayout";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/project/karuna/components/ui/card"; // @ts-ignore - card is not a valid module
import { Badge } from "@/project/karuna/components/ui/badge"; // @ts-ignore - badge is not a valid module
import {
  School,
  Calendar,
  Heart,
  TrendingUp,
  Award,
  FileText,
  CheckCircle,
} from "lucide-react";
import { DynamicModuleService } from "@/services/APIs";
import { useQuery } from "@tanstack/react-query";
import {
  mapWithConcurrency,
  DEFAULT_API_FETCH_CONCURRENCY,
} from "@/project/karuna/utils/mapWithConcurrency";
import { formatDistanceToNow } from "date-fns";

const SCHOOL_PROFILE_SCHEMA_NAME = "School Profile";
const SUBMISSION_SCHEMA_NAME = "Activity Submission";
const ACTIVITY_SCHEMA_NAME = "Activity Management";

// Normalize mobile number helper
const normalizeMobileNumber = (mobileNumber: string): string => {
  return String(mobileNumber ?? "")
    .replace(/\D/g, "")
    .slice(0, 10);
};

// Program name mapping
const programNameMap: Record<string, string> = {
  intraschool: "Organising Intra School Programmes",
  interschool: "Organising Inter School Programmes",
  participationinter: "Participation in Inter-School Competitions/Programmes",
  publicrelation: "Public Relation Programmes",
  karunaclub: "Karuna Club – Internal / External Growth",
  nationallevel: "Organising / Participation in National Level Programme",
};

/** Grid list rows often omit title fields; prefer full record order like SchoolActivityDetail */
const getActivityDisplayTitle = (activity: any): string => {
  const t =
    activity?.activitytitle ??
    activity?.title ??
    activity?.activity_title ??
    activity?.name ??
    activity?.program_name;
  return typeof t === "string" && t.trim() ? t.trim() : "Unknown Activity";
};

const normalizeRecordId = (id: unknown): string =>
  String(id ?? "")
    .trim()
    .toLowerCase();

/** Match submission → activity (grid + full payloads use different shapes) */
const getSubmissionEventId = (submission: any): string => {
  const raw =
    submission?.event_id ??
    submission?.eventid ??
    submission?.activity_id ??
    submission?.activityid ??
    (typeof submission?.activity === "string" ? submission.activity : "") ??
    submission?.basicInfo?.event_id ??
    submission?.basicInfo?.eventid ??
    submission?.basicInfo?.activity_id ??
    "";
  return String(raw).trim();
};

const isSubmissionPendingStatus = (status: unknown): boolean => {
  const s = String(status ?? "pending")
    .toLowerCase()
    .trim();
  return s === "pending" || s === "under review" || s === "under_review";
};

const AdminDashboard = () => {
  // Fetch schools from School Profile schema
  const { data: schoolsData } = useQuery({
    queryKey: ["admin-dashboard-schools", SCHOOL_PROFILE_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await DynamicModuleService.getGridColItemsAllPages(
          SCHOOL_PROFILE_SCHEMA_NAME
        );
      } catch (error) {
        console.error("Error fetching schools:", error);
        return [];
      }
    },
  });

  // Fetch submissions from Activity Submission schema
  const { data: submissionsData } = useQuery({
    queryKey: ["admin-dashboard-submissions", SUBMISSION_SCHEMA_NAME],
    queryFn: async () => {
      try {
        const allSubmissions =
          await DynamicModuleService.getGridColItemsAllPages(
            SUBMISSION_SCHEMA_NAME
          );

        // Fetch full data for recent submissions
        const recentSubmissions = allSubmissions.slice(0, 10); // Get first 10 for recent
        const submissionsWithFullData = await mapWithConcurrency(
          recentSubmissions,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (submission: any) => {
            const submissionId = submission.id ?? submission._id;
            if (!submissionId) {
              return submission;
            }

            try {
              const fullData = await DynamicModuleService.getItemById(
                SUBMISSION_SCHEMA_NAME,
                submissionId
              );
              const data = fullData?.data ?? fullData ?? submission;
              return { ...submission, ...data };
            } catch (_err) {
              return submission;
            }
          }
        );

        return {
          all: allSubmissions,
          recent: submissionsWithFullData,
        };
      } catch (error) {
        console.error("Error fetching submissions:", error);
        return { all: [], recent: [] };
      }
    },
  });

  // Fetch activities — grid rows often omit title/status; merge getItemById like SchoolActivities
  const { data: activitiesData } = useQuery({
    queryKey: ["admin-dashboard-activities", ACTIVITY_SCHEMA_NAME],
    queryFn: async () => {
      try {
        const activitiesList =
          await DynamicModuleService.getGridColItemsAllPages(
            ACTIVITY_SCHEMA_NAME
          );

        const activitiesWithFullData = await mapWithConcurrency(
          activitiesList,
          DEFAULT_API_FETCH_CONCURRENCY,
          async (activity: any) => {
            const activityId = activity.id ?? activity._id;
            if (!activityId) {
              return activity;
            }
            try {
              const fullData = await DynamicModuleService.getItemById(
                ACTIVITY_SCHEMA_NAME,
                activityId
              );
              const data =
                fullData?.data ?? fullData?.result ?? fullData ?? activity;
              return { ...activity, ...data };
            } catch (_err) {
              return activity;
            }
          }
        );

        return activitiesWithFullData;
      } catch (error) {
        console.error("Error fetching activities:", error);
        return [];
      }
    },
  });

  // Fetch school profiles for mapping contact to school name
  const { data: schoolProfilesData } = useQuery({
    queryKey: ["admin-dashboard-school-profiles", SCHOOL_PROFILE_SCHEMA_NAME],
    queryFn: async () => {
      try {
        return await DynamicModuleService.getGridColItemsAllPages(
          SCHOOL_PROFILE_SCHEMA_NAME
        );
      } catch (error) {
        console.error("Error fetching school profiles:", error);
        return [];
      }
    },
  });

  // Create map of contact to school name
  const schoolNameMap = useMemo(() => {
    if (!schoolProfilesData || !Array.isArray(schoolProfilesData)) {
      return new Map<string, string>();
    }
    const map = new Map<string, string>();
    schoolProfilesData.forEach((profile: any) => {
      const contact = normalizeMobileNumber(
        profile.contact ?? profile.school_contact ?? profile.schoolcontact ?? ""
      );
      const schoolName =
        profile.schoolname ?? profile.school_name ?? "Unknown School";
      if (contact) {
        map.set(contact, schoolName);
      }
    });
    return map;
  }, [schoolProfilesData]);

  // Calculate stats
  const stats = useMemo(() => {
    const totalSchools = schoolsData?.length ?? 0;
    const totalSubmissions = submissionsData?.all?.length ?? 0;

    const now = new Date();
    now.setHours(0, 0, 0, 0);

    // "Active" = currently running by date, or explicit active/ongoing/published status (grid often omits status)
    const activeEvents =
      activitiesData?.filter((activity: any) => {
        const status = String(activity.status ?? activity.Status ?? "")
          .toLowerCase()
          .trim();
        if (
          status === "active" ||
          status === "ongoing" ||
          status === "published"
        ) {
          return true;
        }
        const startRaw =
          activity.startdate ?? activity.start_date ?? activity.startDate;
        const endRaw =
          activity.enddate ?? activity.end_date ?? activity.endDate;
        if (!endRaw) {
          return false;
        }
        const end = new Date(endRaw);
        end.setHours(0, 0, 0, 0);
        if (end < now) {
          return false;
        }
        if (!startRaw) {
          return true;
        }
        const start = new Date(startRaw);
        start.setHours(0, 0, 0, 0);
        return start <= now && end >= now;
      }).length ?? 0;

    // Pending reviews — tolerate API status variants
    const pendingSubmissions =
      submissionsData?.all?.filter((s: any) =>
        isSubmissionPendingStatus(s.status ?? s.Status)
      ).length ?? 0;

    return [
      {
        title: "Total Schools",
        value: totalSchools.toLocaleString(),
        icon: School,
        color: "text-blue-600",
        bgColor: "bg-blue-500/10",
      },
      {
        title: "Total Submissions",
        value: totalSubmissions.toLocaleString(),
        icon: FileText,
        color: "text-green-600",
        bgColor: "bg-green-500/10",
      },
      {
        title: "Active Events",
        value: activeEvents.toString(),
        icon: Calendar,
        color: "text-purple-600",
        bgColor: "bg-purple-500/10",
      },
      {
        title: "Pending Reviews",
        value: pendingSubmissions.toString(),
        icon: Heart,
        color: "text-red-600",
        bgColor: "bg-red-500/10",
      },
    ];
  }, [schoolsData, submissionsData, activitiesData]);

  // Transform recent submissions
  const recentSubmissions = useMemo(() => {
    if (!submissionsData?.recent || !Array.isArray(submissionsData.recent)) {
      return [];
    }

    return submissionsData.recent
      .map((submission: any) => {
        const submissionContact = normalizeMobileNumber(
          submission.schoolcontact ??
            submission.contact ??
            submission.school_contact ??
            ""
        );
        const schoolName =
          schoolNameMap.get(submissionContact) ??
          submission.schoolname ??
          "Unknown School";

        // Get program/activity name
        const programValue = submission.program ?? "";
        const programName = programValue
          ? (programNameMap[programValue] ?? programValue)
          : "Unknown Activity";

        // Get submission date
        const submittedAt =
          submission.submitted_at ??
          submission.created_on ??
          submission.created_at ??
          submission.updated_on ??
          "";

        const dateStr = submittedAt
          ? formatDistanceToNow(new Date(submittedAt), { addSuffix: true })
          : "Unknown date";

        return {
          id: submission.id ?? submission._id,
          school: schoolName,
          event: programName,
          status: submission.status ?? "pending",
          date: dateStr,
        };
      })
      .sort((a, b) => {
        // Sort by date (most recent first)
        const dateA = submissionsData.recent.find(
          (s: any) => (s.id ?? s._id) === a.id
        );
        const dateB = submissionsData.recent.find(
          (s: any) => (s.id ?? s._id) === b.id
        );
        const timeA =
          dateA?.created_on ?? dateA?.created_at ?? dateA?.updated_on ?? 0;
        const timeB =
          dateB?.created_on ?? dateB?.created_at ?? dateB?.updated_on ?? 0;
        return new Date(timeB).getTime() - new Date(timeA).getTime();
      })
      .slice(0, 4); // Get 4 most recent
  }, [submissionsData, schoolNameMap]);

  // Transform upcoming events
  const upcomingEvents = useMemo(() => {
    if (!activitiesData || !Array.isArray(activitiesData)) {
      return [];
    }

    const now = new Date();

    return activitiesData
      .filter((activity: any) => {
        // Filter for upcoming or active events
        const endDate =
          activity.enddate ?? activity.end_date ?? activity.enddate;
        if (!endDate) {
          return false;
        }

        const end = new Date(endDate);
        return end >= now; // Event hasn't ended yet
      })
      .map((activity: any) => {
        const title = getActivityDisplayTitle(activity);
        const startDate =
          activity.startdate ?? activity.start_date ?? activity.startDate ?? "";
        const endDate =
          activity.enddate ?? activity.end_date ?? activity.endDate ?? "";

        let dateStr = "";
        if (startDate && endDate) {
          const start = new Date(startDate);
          const end = new Date(endDate);
          dateStr = `${start.toLocaleDateString("en-US", { month: "short", day: "numeric" })} - ${end.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}`;
        } else if (startDate) {
          dateStr = new Date(startDate).toLocaleDateString("en-US", {
            month: "short",
            day: "numeric",
            year: "numeric",
          });
        }

        const activityId = activity.id ?? activity._id;
        const aid = normalizeRecordId(activityId);
        const submissionCount =
          submissionsData?.all?.filter((s: any) => {
            const eid = getSubmissionEventId(s);
            if (!eid || !aid) {
              return false;
            }
            return normalizeRecordId(eid) === aid;
          }).length ?? 0;

        return {
          id: activityId,
          name: title,
          date: dateStr ?? "Date TBD",
          schools: submissionCount,
          chapter: activity.chapter ?? activity.chapter_name ?? "All Chapters",
        };
      })
      .sort((a, b) => {
        // Sort by start date
        const activityA = activitiesData.find(
          (act: any) => (act.id ?? act._id) === a.id
        );
        const activityB = activitiesData.find(
          (act: any) => (act.id ?? act._id) === b.id
        );
        const dateA = activityA?.startdate ?? activityA?.start_date ?? "";
        const dateB = activityB?.startdate ?? activityB?.start_date ?? "";
        if (!dateA) {
          return 1;
        }
        if (!dateB) {
          return -1;
        }
        return new Date(dateA).getTime() - new Date(dateB).getTime();
      })
      .slice(0, 3); // Get 3 upcoming events
  }, [activitiesData, submissionsData]);

  // Calculate additional stats
  const submissionRate = useMemo(() => {
    const totalSchools = schoolsData?.length ?? 0;
    if (totalSchools === 0) {
      return 0;
    }

    // Count unique schools that have submitted
    const schoolContacts = new Set<string>();
    submissionsData?.all?.forEach((submission: any) => {
      const contact = normalizeMobileNumber(
        submission.schoolcontact ??
          submission.contact ??
          submission.school_contact ??
          ""
      );
      if (contact) {
        schoolContacts.add(contact);
      }
    });

    const schoolsWithSubmissions = schoolContacts.size;
    return Math.round((schoolsWithSubmissions / totalSchools) * 100);
  }, [schoolsData, submissionsData]);

  const approvedSubmissions = useMemo(() => {
    return (
      submissionsData?.all?.filter((s: any) => {
        const status = (s.status ?? "").toLowerCase();
        return status === "approved";
      }).length ?? 0
    );
  }, [submissionsData]);

  const _pendingSubmissionsCount = useMemo(() => {
    return (
      submissionsData?.all?.filter((s: any) => {
        const status = (s.status ?? "pending").toLowerCase();
        return status === "pending";
      }).length ?? 0
    );
  }, [submissionsData]);

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold">Dashboard</h1>
            <p className="mt-1 text-muted-foreground">
              Welcome back! Here&apos;s what&apos;s happening with Karuna
              International
            </p>
          </div>
          <Badge variant="outline" className="px-3 py-1 text-sm">
            Academic Year 2024-25
          </Badge>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-4">
          {stats.map(stat => (
            <Card
              key={stat.title}
              className="overflow-hidden transition-shadow hover:shadow-lg"
            >
              <CardContent className="p-6">
                <div className="mb-4 flex items-center justify-between">
                  <div
                    className={`h-12 w-12 rounded-lg ${stat.bgColor} flex items-center justify-center`}
                  >
                    <stat.icon className={`h-6 w-6 ${stat.color}`} />
                  </div>
                </div>
                <div className="mb-1 text-3xl font-bold">{stat.value}</div>
                <div className="text-sm text-muted-foreground">
                  {stat.title}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-xl">Recent Submissions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {recentSubmissions.length > 0 ? (
                  recentSubmissions.map(submission => (
                    <div
                      key={
                        submission.id ??
                        `${submission.school}-${submission.event}-${submission.date}`
                      }
                      className="flex items-center justify-between rounded-lg bg-muted/50 p-3 transition-colors hover:bg-muted"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium">
                          {submission.school}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {submission.event}
                        </p>
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge
                          variant={
                            submission.status === "approved"
                              ? "default"
                              : "secondary"
                          }
                          className="text-xs"
                        >
                          {submission.status === "approved" ? (
                            <>
                              <CheckCircle className="mr-1 h-3 w-3" />
                              Approved
                            </>
                          ) : (
                            "Pending"
                          )}
                        </Badge>
                        <span className="text-xs text-muted-foreground">
                          {submission.date}
                        </span>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    No submissions yet
                  </p>
                )}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-xl">Upcoming Events</CardTitle>
              <Badge variant="outline">{upcomingEvents.length} scheduled</Badge>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {upcomingEvents.length > 0 ? (
                  upcomingEvents.map(event => (
                    <div
                      key={
                        event.id ??
                        `${event.name}-${event.date}-${event.chapter}`
                      }
                      className="flex items-start justify-between rounded-lg bg-muted/50 p-3 transition-colors hover:bg-muted"
                    >
                      <div className="flex-1">
                        <p className="mb-1 text-sm font-medium">{event.name}</p>
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3" />
                          <span>{event.date}</span>
                        </div>
                      </div>
                      <div className="text-right">
                        <Badge variant="secondary" className="mb-1 text-xs">
                          {event.schools} submissions
                        </Badge>
                        <p className="text-xs text-muted-foreground">
                          {event.chapter}
                        </p>
                      </div>
                    </div>
                  ))
                ) : (
                  <p className="py-4 text-center text-sm text-muted-foreground">
                    No upcoming events
                  </p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          <Card className="bg-gradient-hero text-primary-foreground">
            <CardContent className="p-6">
              <div className="mb-2 flex items-center justify-between">
                <TrendingUp className="h-8 w-8" />
                <Badge className="border-0 bg-background/20 text-primary-foreground">
                  +12%
                </Badge>
              </div>
              <div className="mb-1 text-2xl font-bold">{submissionRate}%</div>
              <div className="text-sm opacity-90">Submission Rate</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-green-500 to-emerald-600 text-white">
            <CardContent className="p-6">
              <div className="mb-2 flex items-center justify-between">
                <Award className="h-8 w-8" />
                <Badge className="border-0 bg-white/20 text-white">
                  Top 10%
                </Badge>
              </div>
              <div className="mb-1 text-2xl font-bold">
                {approvedSubmissions}
              </div>
              <div className="text-sm opacity-90">Approved Submissions</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple-500 to-pink-600 text-white">
            <CardContent className="p-6">
              <div className="mb-2 flex items-center justify-between">
                <FileText className="h-8 w-8" />
                <Badge className="border-0 bg-white/20 text-white">
                  This Month
                </Badge>
              </div>
              <div className="mb-1 text-2xl font-bold">
                {(submissionsData?.all?.length ?? 0).toLocaleString()}
              </div>
              <div className="text-sm opacity-90">Total Reports</div>
            </CardContent>
          </Card>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard;
